import { kebabCase } from 'lodash';
import React from 'react';
import IRAContributionsAvailableTip from './IRAContributionsAvailableTip';
import './IRAContributionsEditor.scss';
import AvailableContribution from '../AvailableContribution/AvailableContribution';
import BankAssociableText from '../../../Shared/BankAssociableText/BankAssociableText';
import CurrencyInput from '../../../Shared/CurrencyInput/CurrencyInput';
import If from '../../../Shared/If/If';
import ToolTip from '../../../Shared/ToolTip/ToolTip';
import { formatUnixDate } from '../../../../utils/dates/date-utility';
import Loader from '../../../Shared/Loader/Loader';
import {
  getFundMinimum,
  isNewOrReopen,
  getMismatchBusinessParty,
} from '../../../../utils/funds/funds-utility';
import { isIRA } from '../../../../utils/IRA/IRA-utility';
import {
  coalesce,
  currencyNoPrecision,
  truncateToTwoDecimalFormatted,
} from '../../../../utils/utils';
import { useSelector } from 'react-redux';
import { getIRALimitSearchDataByPoid } from '../../../../redux/modules/funds/IRALimitSearch/IRALimitSearch.selectors';

const YEAR_CURRENT = 'current';
const YEAR_PRIOR = 'prior';

function getIRAContributionDataRow(
  canContribute,
  canContributeBothYears,
  displayTradeDate,
  fund,
  remainingContributions,
  yearPrefix
) {
  const isPriorYear = yearPrefix === YEAR_PRIOR;
  const year = formatUnixDate(displayTradeDate, 'YYYY') - (isPriorYear ? 1 : 0);
  const error = fund[`${yearPrefix}YearError`];
  const fundMin = currencyNoPrecision(
    getFundMinimum(fund, `${yearPrefix}YearLimit`)
  );
  return {
    year: year,
    prefix: yearPrefix,
    amountField: `${yearPrefix}YearAmount`,
    error,
    remainingContribution: remainingContributions,
    canContribute: canContribute,
    validationMessage:
      (canContributeBothYears &&
        isNewOrReopen(fund) &&
        error !== 'Exceeds IRA annual contribution limit') ||
      (error === undefined && fundMin === '$0') // test against formatted, reduce floating point possible issues
        ? ''
        : coalesce(error, `Min. Contribution: ${fundMin}`),
  };
}

function getIRAContributionDataDataRows(
  canContributeCurrentYear,
  canContributePriorYear,
  currentYearInclusiveIRAData,
  displayTradeDate,
  fund,
  priorYearInclusiveIRAData,
  withinIRAPriorYearContributionPeriod
) {
  const data = [];

  if (withinIRAPriorYearContributionPeriod) {
    data.push(
      getIRAContributionDataRow(
        canContributePriorYear,
        canContributePriorYear && canContributeCurrentYear,
        displayTradeDate,
        fund,
        priorYearInclusiveIRAData,
        YEAR_PRIOR
      )
    );
  }

  data.push(
    getIRAContributionDataRow(
      canContributeCurrentYear,
      canContributePriorYear && canContributeCurrentYear,
      displayTradeDate,
      fund,
      currentYearInclusiveIRAData,
      YEAR_CURRENT
    )
  );

  return data;
}

const IRAContributionsEditor = ({
  actions,
  fund: { currentYearInclusiveIRAData, priorYearInclusiveIRAData },
  fund,
  IRALimit,
  IRALimitSearchByPoid,
  maxValue,
  personalData,
  displayTradeDate,
  windowCutoffDate,
}) => {
  const { updateFundAmount } = actions;
  const {
    currentYearRemainingContributions,
    priorYearRemainingContributions,
    withinIRAPriorYearContributionPeriod,
  } = fund;
  const canContributeCurrentYear = currentYearRemainingContributions !== 0;
  const canContributePriorYear = priorYearRemainingContributions !== 0;
  const canContributeBothYears =
    canContributeCurrentYear && canContributePriorYear;
  const newOrReopenIraMinimumFormatted = truncateToTwoDecimalFormatted(
    getFundMinimum(fund, 'currentYearLimit')
  );

  let iraLimitSearch = {};
  const mismatchBusinessParty = getMismatchBusinessParty(fund, personalData);
  const iraLimitSearchPOA = useSelector(getIRALimitSearchDataByPoid);
  if (mismatchBusinessParty) {
    iraLimitSearch =
      IRALimitSearchByPoid[mismatchBusinessParty.businessPartyID];
    if (
      (iraLimitSearch && iraLimitSearch.isLoading) ||
      (iraLimitSearchPOA && iraLimitSearchPOA.isLoading)
    ) {
      return <Loader isVisible />;
    }
  }

  const data = getIRAContributionDataDataRows(
    canContributeCurrentYear,
    canContributePriorYear,
    currentYearInclusiveIRAData,
    displayTradeDate,
    fund,
    priorYearInclusiveIRAData,
    withinIRAPriorYearContributionPeriod
  );
  return (
    <div className="contributions-table">
      <div className="contributions-table-row headers">
        <div className="contributions-table-year-header">Year</div>
        <div className="contributions-table-amount-header">Amount</div>
        <div className="contributions-table-available-header">
          Available Contribution
          <ToolTip message={<IRAContributionsAvailableTip />} />
        </div>
      </div>
      {data.map(
        ({
          amountField,
          error,
          remainingContribution,
          canContribute,
          prefix,
          validationMessage,
          year,
        }) => (
          <If
            test={prefix === YEAR_CURRENT || prefix === YEAR_PRIOR}
            key={year}
          >
            <div key={year} className="contributions-table-row">
              <div className="contributions-table-year-col">{year}</div>
              {canContribute ? (
                <div
                  className={`contributions-table-amount-col e2e-${prefix}Year-${fund.ticker}`}
                >
                  <CurrencyInput
                    ariaLabel={`${
                      prefix === 'prior' ? 'Prior' : 'Current'
                    } Year Contribution`}
                    autoFocus={
                      prefix === 'current' &&
                      !withinIRAPriorYearContributionPeriod
                    }
                    defaultValue={fund[amountField]}
                    error={
                      fund.combinedError ||
                      fund.combinedDailyMaxError ||
                      fund[`${prefix}YearError`] ||
                      (iraLimitSearch && iraLimitSearch.hasFailed) ||
                      (iraLimitSearchPOA && iraLimitSearchPOA.hasFailed)
                    }
                    id={`txt-${fund.agreementID}-${year}`}
                    onChange={(value) =>
                      updateFundAmount(
                        fund.agreementID,
                        value,
                        amountField,
                        fund.BPID,
                        isIRA(fund),
                        mismatchBusinessParty
                          ? mismatchBusinessParty.businessPartyID
                          : undefined
                      )
                    }
                    displayErrorMessage={false}
                  />
                  <div
                    id={`minContribution-${kebabCase(fund.fundName)}`}
                    className={`contributions-table-validation-message ${
                      error ? 'contributions-table-min-error' : ''
                    }`}
                  >
                    {validationMessage}
                  </div>
                </div>
              ) : (
                <div className="contributions-table-amount-col">
                  <span className="contributions-table-empty-space">
                    &mdash;
                  </span>
                </div>
              )}
              <div
                className={`contributions-table-available-col e2e-${prefix}YearAmount-${fund.ticker}`}
              >
                <AvailableContribution
                  actions={actions}
                  amount={remainingContribution}
                  currentYear={prefix === YEAR_CURRENT}
                  displayTradeDate={displayTradeDate}
                  fund={fund}
                  IRAContribution={IRALimit.IRAContribution}
                  IRALimit={IRALimit}
                  agreementType={fund.agreementType}
                  personalData={personalData}
                  windowCutoffDate={windowCutoffDate}
                  iraLimitSearch={iraLimitSearch || iraLimitSearchPOA}
                />
              </div>
            </div>
          </If>
        )
      )}

      <If
        test={
          isNewOrReopen(fund) &&
          withinIRAPriorYearContributionPeriod &&
          canContributeBothYears &&
          !fund.combinedDailyMaxError
        }
      >
        <div
          className={`contributions-table-combined-ira-message ${
            fund.combinedError ? 'contributions-table-error' : ''
          }`}
        >
          Minimum required combined contribution{' '}
          {fund.newFund ? 'on new' : 'to reopen'} IRAs is{' '}
          {newOrReopenIraMinimumFormatted}
        </div>
      </If>
      <If test={fund.combinedDailyMaxError}>
        <div
          className={`contributions-table-combined-ira-message ${
            fund.combinedDailyMaxError ? 'contributions-table-error' : ''
          }`}
        >
          Daily Max: {currencyNoPrecision(maxValue)}
        </div>
      </If>
      <BankAssociableText
        containerClass="retirement"
        showText={fund.bankAssociable}
      />
    </div>
  );
};

export default IRAContributionsEditor;
